import {
  ArchivalAidMetadata,
  ArchivalCollection,
  EadNode,
  EadNodeParentView,
  Result,
} from 'types';
import { GridBreadcrumb } from '../grid-types';
import { useQuery } from 'react-query';
import { queryClient } from 'index';
import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { SortOrder } from 'enums';
import { useEadTreeContext } from './EadTreeCtx';

const CONTEXT = process.env.PUBLIC_URL;

const collectionApi = CONTEXT + '/api/archival-collection/list';
const aidsApi = CONTEXT + '/api/archival-aid-metadata/list';

export const useArchivalCollectionsList = () => {
  const response = useQuery(['collections'], () =>
    fetch(collectionApi, {
      method: 'POST',
      body: JSON.stringify({
        sort: [{ field: 'name', order: 'ASC' as SortOrder }],
        size: -1,
      }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    }).then(async (r) => (await r.json()) as Result<ArchivalCollection>)
  );
  return response;
};

export const useArchivalAidsList = () => {
  const response = useQuery(['aids'], () =>
    fetch(aidsApi, {
      method: 'POST',
      body: JSON.stringify({
        sort: [{ field: 'name', order: 'ASC' as SortOrder }],
        size: -1,
      }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    }).then(async (r) => (await r.json()) as Result<ArchivalAidMetadata>)
  );
  return response;
};

export const useArchivalCollections = () => {
  const { replace } = useHistory();
  const etctx = useEadTreeContext();
  const { setCurrentArchivalCollection, setCrumbs, originatorFilter } = etctx;
  const { id } = useParams<{ id: string }>();

  const response = useQuery(['collections', originatorFilter?.params], () =>
    fetch(collectionApi, {
      method: 'POST',
      body: JSON.stringify({
        sort: [{ field: 'name', order: 'ASC' as SortOrder }],
        size: -1,
        ...originatorFilter?.params,
      }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    }).then(async (r) => (await r.json()) as Result<ArchivalCollection>)
  );

  useEffect(() => {
    if (
      response.isSuccess &&
      response.data.items &&
      !originatorFilter &&
      !id &&
      response.data.items?.length > 0
    ) {
      if (response.data.items?.[0]?.id) {
        replace(`/fondy/0/${response.data.items[0].id}`);
      }
    } else {
      if (
        originatorFilter &&
        response.isSuccess &&
        (!response.data.items || response.data.items?.length === 0)
      ) {
        setCurrentArchivalCollection?.(undefined);
        setCrumbs?.(undefined);
      } else if (response?.data?.items?.[0]?.id && !id) {
        replace(`/fondy/0/${response.data.items[0].id}`);
      }
    }
  }, [
    response,
    replace,
    id,
    setCurrentArchivalCollection,
    setCrumbs,
    originatorFilter,
  ]);

  return {
    collections: response.data?.items ?? [],
    loading: response.isLoading,
  };
};

export const useArchivalAids = () => {
  const { replace } = useHistory();
  const etctx = useEadTreeContext();
  const { setCurrentArchivalAid, setCrumbs, originatorFilter } = etctx;
  const { id } = useParams<{ id: string }>();

  const response = useQuery(['aids', originatorFilter?.params], () =>
    fetch(aidsApi, {
      method: 'POST',
      body: JSON.stringify({
        sort: [{ field: 'name', order: 'ASC' as SortOrder }],
        size: -1,
        ...originatorFilter?.params,
      }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    }).then(async (r) => (await r.json()) as Result<ArchivalAidMetadata>)
  );

  useEffect(() => {
    if (
      response.isSuccess &&
      response.data.items &&
      !originatorFilter &&
      !id &&
      response.data.items?.length > 0
    ) {
      if (response.data.items?.[0]?.id) {
        replace(`/pomucky/0/${response.data.items[0].id}`);
      }
    } else {
      if (
        originatorFilter &&
        response.isSuccess &&
        (!response.data.items || response.data.items?.length === 0)
      ) {
        setCurrentArchivalAid?.(undefined);
        setCrumbs?.(undefined);
      } else if (response?.data?.items?.[0]?.id && !id) {
        replace(`/pomucky/0/${response.data.items[0].id}`);
      }
    }
  }, [
    response,
    replace,
    id,
    setCurrentArchivalAid,
    setCrumbs,
    originatorFilter,
  ]);

  return {
    aids: response.data?.items ?? [],
    loading: response.isLoading,
  };
};

export const useArchivalAidsListByCollection = (
  collectionId: string,
  active: boolean
) => {
  const response = useQuery(
    ['aids', collectionId],
    () =>
      fetch(aidsApi, {
        method: 'POST',
        body: JSON.stringify({
          filters: [
            {
              field: 'archivalCollection.id',
              operation: 'EQ',
              value: collectionId,
            },
          ],
          size: -1,
        }),

        headers: new Headers({ 'Content-Type': 'application/json' }),
      }).then(async (r) => (await r.json()) as Result<ArchivalAidMetadata>),
    { enabled: active }
  );

  return {
    aids: response.data?.items ?? [],
    loading: response.isLoading,
  };
};

export const singleFetch = async <T>(url: string, key: string) => {
  try {
    const resp = await queryClient.fetchQuery([key], () =>
      fetch(CONTEXT + url, {
        headers: new Headers({ 'Content-Type': 'application/json' }),
      }).then(async (r) => ((await r.json()) as unknown) as T)
    );
    return resp;
  } catch (error) {
    console.log({ error });
    return (undefined as unknown) as T;
  }
};

export const isInPath = (id: string, crumbs: GridBreadcrumb[]) =>
  crumbs?.some((c) => c.id === id);

export const getNodeId = (
  node?: EadNode | EadNodeParentView
): string | undefined => node?.baseIdentifier ?? node?.id;
